import React, { useState } from "react";
import Icon from "../../../components/AppIcon";

const PreparationGuide = ({ preparationData = {} }) => {
  const [activeSection, setActiveSection] = useState("fitness");

  const sections = [
    { id: "fitness", label: "Fitness Training", icon: "Activity" },
    { id: "gear", label: "Essential Gear", icon: "Backpack" },
    { id: "documents", label: "Documents & Permits", icon: "FileText" },
    { id: "health", label: "Health & Safety", icon: "Heart" },
  ];

  const fitness = preparationData?.fitness || {};
  const gear = preparationData?.gear || {};
  const documents = preparationData?.documents || {};
  const health = preparationData?.health || {};

  return (
    <div className="bg-[#fafaf7] py-16 md:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="section-heading text-foreground mb-3">
            <span className="text-primary">Preparation</span> Guide
          </h2>
          <div className="w-16 h-1 bg-primary rounded-full mx-auto mb-4" />
          <p className="text-lg text-text-secondary max-w-3xl mx-auto leading-relaxed">
            Proper preparation is key to a successful and enjoyable trek.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {sections.map((section) => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all duration-200 ${
                activeSection === section.id
                  ? "bg-primary text-white shadow-sm"
                  : "bg-white text-text-secondary hover:text-foreground hover:bg-white border border-border/50"
              }`}
            >
              <Icon name={section.icon} size={18} />
              <span>{section.label}</span>
            </button>
          ))}
        </div>

        <div className="max-w-4xl mx-auto">
          {activeSection === "fitness" && (
            <div className="animate-fade-in space-y-8">
              <div className="bg-white rounded-2xl border border-border/50 p-8">
                <h3 className="font-headline text-2xl font-semibold text-foreground mb-6">
                  {fitness.title || "Physical Fitness Training"}
                </h3>

                <div className="grid md:grid-cols-2 gap-8">
                  <div>
                    <h4 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                      <Icon name="Calendar" size={20} className="text-primary" />
                      <span>Training Plan</span>
                    </h4>

                    <div className="space-y-6">
                      {(fitness.trainingPlan || []).map((phase, idx) => (
                        <div key={idx} className="border-l-4 border-primary pl-4">
                          <h5 className="font-semibold text-foreground">{phase.phase}</h5>
                          <p className="text-sm text-text-secondary mb-2">{phase.duration}</p>
                          <ul className="text-sm text-text-secondary space-y-1 list-disc pl-5">
                            {(phase.activities || []).map((act, i) => (
                              <li key={i}>{act}</li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                      <Icon name="Target" size={20} className="text-primary" />
                      <span>Fitness Goals</span>
                    </h4>

                    <div className="space-y-4">
                      {(fitness.goals || []).map((goal, idx) => (
                        <div key={idx} className="bg-[#fafaf7] rounded-xl p-4">
                          <div className="flex items-center gap-3 mb-2">
                            <Icon name={goal.icon || "Target"} size={18} className="text-accent" />
                            <span className="font-semibold text-foreground">{goal.title}</span>
                          </div>
                          <p className="text-sm text-text-secondary">{goal.description}</p>
                          {goal.target && (
                            <p className="text-sm text-primary mt-1 font-medium">
                              Target: {goal.target}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {fitness.minimumRequirements?.length > 0 && (
                  <div className="mt-8">
                    <h4 className="text-lg font-semibold text-foreground mb-4">Minimum Requirements</h4>
                    <ul className="space-y-2 text-text-secondary">
                      {fitness.minimumRequirements.map((req, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <div className="w-5 h-5 rounded-full bg-success/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Icon name="Check" size={12} className="text-success" />
                          </div>
                          <span className="text-sm">{req}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeSection === "gear" && (
            <div className="animate-fade-in space-y-6">
              <div className="bg-white rounded-2xl border border-border/50 p-8">
                <h3 className="font-headline text-2xl font-semibold text-foreground mb-6">
                  {gear.title || "Essential Gear Checklist"}
                </h3>

                {(gear.categories || []).map((cat, idx) => (
                  <div key={idx} className="mb-8 last:mb-0">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                        <Icon name={cat.icon || "Backpack"} size={20} className="text-primary" />
                      </div>
                      <h4 className="font-headline text-xl font-semibold text-foreground">{cat.name}</h4>
                    </div>

                    <div className="grid md:grid-cols-2 gap-3">
                      {(cat.items || []).map((item, i) => (
                        <div key={i} className="flex items-start gap-3 p-4 bg-[#fafaf7] rounded-xl">
                          <Icon
                            name={item.essential ? "AlertCircle" : "Check"}
                            size={18}
                            className={`mt-1 flex-shrink-0 ${item.essential ? "text-error" : "text-success"}`}
                          />
                          <div>
                            <span className="font-medium text-foreground text-sm block">
                              {item.name}
                              {item.essential && (
                                <span className="ml-2 text-xs bg-error/10 text-error px-2 py-0.5 rounded-full">Essential</span>
                              )}
                            </span>
                            {item.note && (
                              <p className="text-sm text-text-secondary mt-1">{item.note}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                {gear.rentalOptions?.length > 0 && (
                  <div className="mt-8 pt-6 border-t border-border/50">
                    <h4 className="text-lg font-semibold text-foreground mb-4">Rental Options</h4>
                    <div className="grid md:grid-cols-2 gap-3">
                      {gear.rentalOptions.map((opt, i) => (
                        <div key={i} className="flex items-center justify-between p-4 bg-[#fafaf7] rounded-xl">
                          <span className="font-medium text-sm">{opt.item}</span>
                          <span className="text-accent font-semibold text-sm">{opt.cost}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeSection === "documents" && (
            <div className="animate-fade-in">
              <div className="bg-white rounded-2xl border border-border/50 p-8">
                <h3 className="font-headline text-2xl font-semibold text-foreground mb-6">
                  {documents.title || "Required Documents"}
                </h3>

                <div className="space-y-6">
                  {(documents.items || []).map((doc, idx) => (
                    <div key={idx} className="border border-border/50 rounded-xl p-6 hover:shadow-sm transition-shadow">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Icon name={doc.icon || "FileText"} size={22} className="text-primary" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-headline text-lg font-semibold text-foreground mb-2">{doc.title}</h4>
                          <p className="text-text-secondary text-sm mb-3">{doc.description}</p>

                          {doc.cost && (
                            <div className="flex items-center gap-2 mb-2">
                              <Icon name="DollarSign" size={14} />
                              <span className="font-medium text-sm">Cost: {doc.cost}</span>
                            </div>
                          )}

                          {doc.requirements?.length > 0 && (
                            <div className="mt-4">
                              <span className="font-medium text-foreground text-sm block mb-2">Requirements:</span>
                              <ul className="space-y-1">
                                {doc.requirements.map((req, i) => (
                                  <li key={i} className="flex items-start gap-2 text-text-secondary text-sm">
                                    <Icon name="ArrowRight" size={12} className="mt-0.5 flex-shrink-0" />
                                    <span>{req}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeSection === "health" && (
            <div className="animate-fade-in space-y-8">
              <div className="bg-white rounded-2xl border border-border/50 p-8">
                <h3 className="font-headline text-2xl font-semibold text-foreground mb-6">
                  {health.title || "Health & Altitude Preparation"}
                </h3>

                {health.altitudeSickness && (
                  <div className="mb-10">
                    <h4 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
                      <Icon name="Shield" size={20} className="text-success" />
                      <span>Altitude Sickness Prevention & Management</span>
                    </h4>

                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="bg-[#fafaf7] p-5 rounded-xl">
                        <h5 className="font-semibold mb-3 text-success">Prevention</h5>
                        <ul className="space-y-2 text-sm text-text-secondary">
                          {(health.altitudeSickness.prevention || []).map((p, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <Icon name="Check" size={14} className="text-success mt-0.5" />
                              <span>{p}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="bg-[#fafaf7] p-5 rounded-xl">
                        <h5 className="font-semibold mb-3 text-warning">Symptoms</h5>
                        <ul className="space-y-2 text-sm text-text-secondary">
                          {(health.altitudeSickness.symptoms || []).map((s, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <Icon name="AlertCircle" size={14} className="text-warning mt-0.5" />
                              <span>{s}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="bg-[#fafaf7] p-5 rounded-xl">
                        <h5 className="font-semibold mb-3 text-accent">Treatment</h5>
                        <ul className="space-y-2 text-sm text-text-secondary">
                          {(health.altitudeSickness.treatment || []).map((t, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <Icon name="Plus" size={14} className="text-accent mt-0.5" />
                              <span>{t}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PreparationGuide;
